import { Component, OnDestroy, OnInit, computed, signal } from '@angular/core';
import { CommonModule } from '@angular/common';
import { OrdersApiService, OrderApiItem, OrderApiItemLine, OrderWorkflowStatus } from '../../shared/orders-api.service';
import { RealtimeStompService } from '../../shared/realtime-stomp.service';
import { getWebSocketUrl } from '../../shared/runtime-config';
import { readStoredOrgId } from '../../shared/auth.service';
import { formatPhoneNumber } from '../../shared/phone-format';

interface OrderView {
  orderId: string;
  phone: string;
  customerName: string;
  displayName: string;
  currency: string;
  items: OrderApiItemLine[];
  total: number;
  status: OrderWorkflowStatus;
  createdAt: string;
}

@Component({
  standalone: true,
  selector: 'app-orders-page',
  imports: [CommonModule],
  template: `
  <div class="page">
    <div class="hero">
      <div class="ic">🧾</div>
      <div>
        <div class="h1">Orders</div>
        <div class="h2">Sales/order pipeline from backend API + realtime updates</div>
      </div>
    </div>

    <div class="syncMsg" *ngIf="loading()">Loading orders from backend...</div>
    <div class="syncMsg" *ngIf="!loading() && syncMessage()">{{ syncMessage() }}</div>

    <div class="stats">
      <div class="stat">
        <div class="k">Total Orders</div>
        <div class="v">{{ filteredOrders().length }}</div>
      </div>
      <div class="stat">
        <div class="k">Revenue (Total)</div>
        <div class="v">{{ money(totalRevenue()) }}</div>
      </div>
      <div class="stat">
        <div class="k">Realized Revenue</div>
        <div class="v">{{ money(realizedRevenue()) }}</div>
      </div>
      <div class="stat">
        <div class="k">Pending Revenue</div>
        <div class="v">{{ money(pendingRevenue()) }}</div>
      </div>
      <div class="stat">
        <div class="k">Rejected</div>
        <div class="v">{{ rejectedCount() }}</div>
      </div>
    </div>

    <div class="filters card">
      <div class="fg">
        <div class="fk">Status</div>
        <div class="chips">
          <button
            type="button"
            class="chip"
            *ngFor="let s of statusFilterOptions"
            [class.active]="selectedStatus()===s"
            (click)="selectedStatus.set(s)">
            {{ s }}
            <span class="count" *ngIf="s !== 'All Statuses'">{{ statusCount(s) }}</span>
          </button>
        </div>
      </div>
      <div class="fg">
        <div class="fk">Date</div>
        <div class="chips">
          <button
            type="button"
            class="chip"
            *ngFor="let d of dateFilterOptions"
            [class.active]="selectedDate()===d"
            (click)="selectedDate.set(d)">
            {{ d }}
          </button>
        </div>
      </div>
    </div>

    <div class="card list">
      <div class="listTop">
        <div class="muted">
          Showing {{ pageStartIndex() }}-{{ pageEndIndex() }} of {{ filteredOrders().length }}
        </div>
        <div class="pageSizeWrap">
          <label for="pageSize" class="muted">Rows</label>
          <select id="pageSize" class="pageSizeSelect" [value]="pageSize()" (change)="onPageSizeSelect($event)">
            <option *ngFor="let size of pageSizeOptions" [value]="size">{{ size }}</option>
          </select>
        </div>
      </div>
      <div class="tableWrap">
        <div class="head">
          <div>Order</div>
          <div>Customer</div>
          <div>Items</div>
          <div>Total</div>
          <div>Stage</div>
          <div>Created</div>
        </div>
        <div class="row" *ngFor="let order of pagedOrders()">
          <div class="name orderId">{{ order.orderId }}</div>
          <div>
            <div class="name">{{ order.customerName }}</div>
            <div class="muted">{{ order.displayName || '-' }}</div>
            <div class="muted">{{ formatPhone(order.phone) }}</div>
          </div>
          <div>
            <div class="itemsWrap">
              <div class="itemLine" *ngFor="let item of order.items">
                <span class="itemName">{{ item.name }}</span>
                <span class="muted">x{{ item.quantity }}</span>
                <span class="itemPrice">{{ money(item.price, order.currency) }}</span>
              </div>
            </div>
          </div>
          <div class="name">{{ money(order.total, order.currency) }}</div>
          <div>
            <span class="statusBadge" [class]="'statusBadge status-' + order.status">{{ order.status }}</span>
            <select class="statusSelect" [value]="order.status" (change)="onStatusSelect(order.orderId, $event)">
              <option *ngFor="let s of orderStatusOptions" [value]="s">{{ s }}</option>
            </select>
          </div>
          <div class="muted">{{ hhmm(order.createdAt) }}</div>
        </div>
        <div class="empty" *ngIf="!filteredOrders().length">No orders found for selected filters.</div>
      </div>
      <div class="pager" *ngIf="filteredOrders().length">
        <button type="button" class="pagerBtn" [disabled]="effectivePage() <= 1" (click)="goToPrevPage()">Previous</button>
        <div class="muted pageInfo">Page {{ effectivePage() }} / {{ totalPages() }}</div>
        <button type="button" class="pagerBtn" [disabled]="effectivePage() >= totalPages()" (click)="goToNextPage()">Next</button>
      </div>
    </div>

  </div>
  `,
  styles: [`
    .page{flex:1; padding:16px; overflow:auto}
    .hero{display:flex; gap:12px; align-items:center; padding:14px; border-radius:18px;
      border:1px solid var(--border); background:rgba(17,27,33,.55)}
    .ic{width:44px;height:44px;border-radius:16px;display:flex;align-items:center;justify-content:center;
      background:rgba(0,168,132,.16);border:1px solid rgba(0,168,132,.35);font-size:20px}
    .h1{font-size:18px;font-weight:900}
    .h2{margin-top:2px;color:var(--muted);font-size:12px}
    .syncMsg{margin-top:10px;font-size:12px;color:var(--muted)}
    .stats{margin-top:12px;display:grid;gap:12px;grid-template-columns:repeat(5,minmax(0,1fr))}
    .stat{padding:12px;border-radius:16px;border:1px solid var(--border);background:rgba(17,27,33,.55)}
    .k{font-size:12px;color:var(--muted)}
    .v{font-size:24px;font-weight:900;margin-top:4px}
    .card{margin-top:12px;padding:10px;border-radius:18px;border:1px solid var(--border);background:rgba(17,27,33,.55)}
    .listTop{display:flex;justify-content:space-between;align-items:center;gap:12px;padding:8px 10px}
    .tableWrap{
      max-height:min(58vh, 620px);
      overflow:auto;
      padding-bottom:4px;
    }
    .pageSizeWrap{display:flex;align-items:center;gap:8px}
    .pageSizeSelect{
      border:1px solid var(--border);
      background:var(--input);
      color:var(--text);
      border-radius:10px;
      height:32px;
      padding:0 8px;
      font-size:12px;
    }
    .filters{display:grid;grid-template-columns:1fr 1fr;gap:12px}
    .fg{padding:4px}
    .fk{font-size:12px;color:var(--muted);font-weight:700;margin-bottom:8px}
    .chips{display:flex;gap:8px;flex-wrap:wrap}
    .chip{
      border:1px solid var(--border);background:transparent;color:var(--muted);border-radius:999px;padding:4px 10px;
      font-size:12px;cursor:pointer;display:inline-flex;align-items:center;gap:6px
    }
    .chip:hover{color:var(--text);border-color:rgba(0,168,132,.35);background:rgba(0,168,132,.08)}
    .chip.active{color:var(--text);border-color:rgba(0,168,132,.45);background:rgba(0,168,132,.16)}
    .count{font-size:10px;font-weight:800;opacity:.9}
    .head,.row{display:grid;grid-template-columns:130px 1.4fr 2.1fr .9fr 1fr .7fr;gap:10px;align-items:start}
    .head{padding:8px 10px;font-size:11px;color:var(--muted);font-weight:700;text-transform:uppercase;letter-spacing:.4px}
    .tableWrap .head{
      position:sticky;
      top:0;
      z-index:1;
      background:rgba(17,27,33,.95);
      border-bottom:1px solid rgba(42,57,66,.45);
    }
    .row{padding:10px;border-radius:12px}
    .row:hover{background:rgba(42,57,66,.35)}
    .name{font-weight:800}
    .orderId{
      font-family:ui-monospace,SFMono-Regular,Menlo,monospace;
      font-size:12px;
      min-width:0;
      white-space:nowrap;
      overflow:hidden;
      text-overflow:ellipsis;
    }
    .itemsWrap{display:grid;gap:6px}
    .itemLine{display:grid;grid-template-columns:minmax(0,1fr) auto auto;gap:8px;align-items:center}
    .itemName{font-size:12px}
    .itemPrice{font-size:12px;font-weight:700}
    .statusBadge{
      display:inline-flex;
      font-size:11px;
      padding:3px 8px;
      border-radius:999px;
      border:1px solid transparent;
      text-transform:capitalize;
      font-weight:700;
      margin-right:8px;
    }
    .status-placed{color:#fde68a;border-color:rgba(245,158,11,.4);background:rgba(245,158,11,.16)}
    .status-confirmed{color:#93c5fd;border-color:rgba(59,130,246,.4);background:rgba(59,130,246,.16)}
    .status-paid{color:#86efac;border-color:rgba(34,197,94,.4);background:rgba(34,197,94,.16)}
    .status-fulfilled{color:#6ee7b7;border-color:rgba(16,185,129,.4);background:rgba(16,185,129,.16)}
    .status-rejected{color:#fda4af;border-color:rgba(244,63,94,.4);background:rgba(244,63,94,.16)}
    .statusSelect{
      margin-top:6px;
      width:100%;
      border:1px solid var(--border);
      background:var(--input);
      color:var(--text);
      border-radius:10px;
      height:34px;
      padding:0 8px;
      text-transform:capitalize;
      font-size:12px;
    }
    .empty,.muted{color:var(--muted)}
    .empty{padding:12px;text-align:center}
    .pager{
      margin-top:10px;
      display:flex;
      justify-content:center;
      gap:10px;
      align-items:center;
      padding:8px 10px;
    }
    .pagerBtn{
      border:1px solid var(--border);
      background:transparent;
      color:var(--text);
      border-radius:10px;
      height:34px;
      padding:0 12px;
      cursor:pointer;
      font-size:12px;
    }
    .pagerBtn:hover{border-color:rgba(0,168,132,.35);background:rgba(0,168,132,.10)}
    .pagerBtn:disabled{
      opacity:.45;
      cursor:not-allowed;
    }
    .pageInfo{min-width:90px;text-align:center}
    :host-context(body.light) .hero,
    :host-context(body.light) .stat,
    :host-context(body.light) .card{
      background:rgba(255,255,255,.92);
      border-color:rgba(15,23,42,.10);
    }
    :host-context(body.light) .row:hover{background:rgba(15,23,42,.04)}
    :host-context(body.light) .chip{
      color:#475569;
      border-color:rgba(15,23,42,.14);
      background:rgba(15,23,42,.02);
    }
    :host-context(body.light) .chip:hover{color:#0f172a;background:rgba(2,132,199,.08);border-color:rgba(2,132,199,.22)}
    :host-context(body.light) .chip.active{color:#0f172a;background:rgba(2,132,199,.14);border-color:rgba(2,132,199,.30)}
    :host-context(body.light) .tableWrap .head{
      background:rgba(255,255,255,.98);
      border-bottom-color:rgba(15,23,42,.10);
    }
    :host-context(body.light) .h1,
    :host-context(body.light) .name,
    :host-context(body.light) .v{color:#0f172a}
    :host-context(body.light) .k,
    :host-context(body.light) .h2,
    :host-context(body.light) .muted,
    :host-context(body.light) .empty,
    :host-context(body.light) .fk,
    :host-context(body.light) .head{color:#475569}
    :host-context(body.light) .statusSelect{background:#fff;color:#0f172a;border-color:rgba(15,23,42,.16)}
    :host-context(body.light) .pageSizeSelect{background:#fff;color:#0f172a;border-color:rgba(15,23,42,.16)}
    @media (max-width: 1100px){
      .stats{grid-template-columns:repeat(2,minmax(0,1fr))}
      .filters{grid-template-columns:1fr}
      .head,.row{grid-template-columns:120px 1.2fr 1.8fr .9fr 1fr .8fr}
    }
    @media (max-width: 900px){
      .stats{grid-template-columns:1fr 1fr}
      .listTop{padding-bottom:2px}
      .head{display:none}
      .row{grid-template-columns:1fr;gap:6px}
    }
  `]
})
export class OrdersPageComponent implements OnInit, OnDestroy {
  private unsubscribeRealtime: (() => void) | null = null;

  orderStatusOptions: OrderWorkflowStatus[] = ['placed', 'confirmed', 'paid', 'fulfilled', 'rejected'];
  statusFilterOptions: Array<'All Statuses' | OrderWorkflowStatus> = ['All Statuses', ...this.orderStatusOptions];
  pageSizeOptions = [5, 10, 20];
  pageSize = signal<number>(10);
  currentPage = signal<number>(1);

  orders = signal<OrderView[]>([]);
  statusByOrderId = signal<Record<string, OrderWorkflowStatus>>({});
  loading = signal<boolean>(false);
  syncMessage = signal<string>('');

  selectedStatus = signal<'All Statuses' | OrderWorkflowStatus>('All Statuses');
  selectedDate = signal<string>('All Time');
  dateFilterOptions = ['All Time', 'Today', 'Last 7 Days', 'Last 30 Days'] as const;

  allOrders = computed<OrderView[]>(() => {
    const statusOverrides = this.statusByOrderId();
    return this.orders().map(order => ({
      ...order,
      status: statusOverrides[order.orderId] || order.status,
    }));
  });

  filteredOrders = computed(() => {
    const status = this.selectedStatus();
    const date = this.selectedDate();

    return this.allOrders().filter(order => {
      const statusOk = status === 'All Statuses' || order.status === status;
      if (!statusOk) return false;
      return this.isInDateFilter(order.createdAt, date);
    });
  });

  totalPages = computed(() => Math.max(1, Math.ceil(this.filteredOrders().length / this.pageSize())));
  effectivePage = computed(() => Math.min(this.currentPage(), this.totalPages()));
  pagedOrders = computed(() => {
    const page = this.effectivePage();
    const start = (page - 1) * this.pageSize();
    return this.filteredOrders().slice(start, start + this.pageSize());
  });
  pageStartIndex = computed(() => {
    if (!this.filteredOrders().length) return 0;
    return ((this.effectivePage() - 1) * this.pageSize()) + 1;
  });
  pageEndIndex = computed(() => {
    const end = this.effectivePage() * this.pageSize();
    return Math.min(end, this.filteredOrders().length);
  });

  totalRevenue = computed(() =>
    this.filteredOrders().reduce((sum, o) => sum + o.total, 0)
  );
  realizedRevenue = computed(() =>
    this.filteredOrders()
      .filter(o => o.status === 'paid' || o.status === 'fulfilled')
      .reduce((sum, o) => sum + o.total, 0)
  );
  pendingRevenue = computed(() =>
    this.filteredOrders()
      .filter(o => o.status === 'placed' || o.status === 'confirmed')
      .reduce((sum, o) => sum + o.total, 0)
  );
  rejectedCount = computed(() =>
    this.filteredOrders().filter(o => o.status === 'rejected').length
  );

  constructor(
    private readonly ordersApi: OrdersApiService,
    private readonly realtime: RealtimeStompService
  ) {}

  async ngOnInit(): Promise<void> {
    await this.reloadOrders();
    this.bindRealtime();
  }

  ngOnDestroy(): void {
    this.unsubscribeRealtime?.();
    this.unsubscribeRealtime = null;
  }

  statusCount(status: OrderWorkflowStatus): number {
    return this.allOrders().filter(o => o.status === status).length;
  }

  isInDateFilter(ts: string, filter: string): boolean {
    if (filter === 'All Time') return true;
    const d = new Date(ts);
    const now = new Date();
    const dayMs = 24 * 60 * 60 * 1000;
    if (filter === 'Today') {
      return d.getFullYear() === now.getFullYear()
        && d.getMonth() === now.getMonth()
        && d.getDate() === now.getDate();
    }
    const diff = now.getTime() - d.getTime();
    if (filter === 'Last 7 Days') return diff <= 7 * dayMs;
    if (filter === 'Last 30 Days') return diff <= 30 * dayMs;
    return true;
  }

  money(amount: number, currency = 'USD'): string {
    return new Intl.NumberFormat('en-US', { style: 'currency', currency, maximumFractionDigits: 0 }).format(amount);
  }

  hhmm(ts: string): string {
    const d = new Date(ts);
    if (Number.isNaN(d.getTime())) return '-';
    return d.toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' });
  }

  formatPhone(value: string): string {
    return formatPhoneNumber(value);
  }

  onStatusChange(orderId: string, status: OrderWorkflowStatus): void {
    this.statusByOrderId.update(current => ({ ...current, [orderId]: status }));
  }

  async onStatusSelect(orderId: string, event: Event): Promise<void> {
    const target = event.target as HTMLSelectElement | null;
    const selected = target?.value as OrderWorkflowStatus | undefined;
    if (!selected || !this.orderStatusOptions.includes(selected)) return;
    const order = this.allOrders().find(row => row.orderId === orderId);
    if (!order) return;

    const previousStatus = order.status;
    if (previousStatus === selected) return;

    this.onStatusChange(orderId, selected);
    try {
      await this.ordersApi.notifyStatusChange(order, selected);
      this.syncMessage.set(`Updated ${orderId} to ${selected}.`);
    } catch (error) {
      this.onStatusChange(orderId, previousStatus);
      this.syncMessage.set(`Failed to sync status update for ${orderId}.`);
      console.error('Order status update failed', { orderId, selected, error });
    }
  }

  onPageSizeSelect(event: Event): void {
    const target = event.target as HTMLSelectElement | null;
    const selected = Number(target?.value);
    if (!selected || !this.pageSizeOptions.includes(selected)) return;
    this.pageSize.set(selected);
    this.currentPage.set(1);
  }

  goToPrevPage(): void {
    if (this.currentPage() <= 1) return;
    this.currentPage.update(current => current - 1);
  }

  goToNextPage(): void {
    if (this.effectivePage() >= this.totalPages()) return;
    this.currentPage.update(current => current + 1);
  }

  private async reloadOrders(): Promise<void> {
    try {
      this.loading.set(true);
      const rows = await this.ordersApi.listOrders();
      this.orders.set(rows.map(row => this.toView(row)));
      this.syncMessage.set(`Loaded ${rows.length} orders from backend.`);
    } catch (error) {
      this.syncMessage.set('Failed to load orders from backend.');
      console.error('Orders load failed', error);
    } finally {
      this.loading.set(false);
    }
  }

  private bindRealtime(): void {
    const orgId = readStoredOrgId();
    const wsUrl = getWebSocketUrl();
    this.realtime.connect(wsUrl);
    this.unsubscribeRealtime = this.realtime.subscribe(`/topic/orders/${orgId}`, payload => {
      this.handleRealtimeEvent(payload);
    });
  }

  private handleRealtimeEvent(payload: unknown): void {
    if (!payload || typeof payload !== 'object') return;
    const event = payload as Record<string, unknown>;
    const eventType = String(event['eventType'] || '').toUpperCase();
    if (eventType !== 'STATUS_UPDATED') return;

    const orderPayload = event['order'] || event['payload'] || payload;
    const mapped = this.ordersApi.mapOrder(orderPayload);
    if (mapped) {
      this.upsertOrder(mapped);
      this.syncMessage.set(`Realtime update received for ${mapped.orderId}.`);
      return;
    }

    const orderId = String(event['orderId'] || '').trim();
    const statusRaw = String(event['status'] || event['orderStatus'] || '').trim().toLowerCase();
    if (!orderId || !statusRaw) return;

    const normalizedStatus = this.orderStatusOptions.find(s => s === statusRaw as OrderWorkflowStatus);
    if (!normalizedStatus) return;
    this.onStatusChange(orderId, normalizedStatus);
    this.syncMessage.set(`Realtime status update received for ${orderId}.`);
  }

  private upsertOrder(order: OrderApiItem): void {
    const view = this.toView(order);
    this.orders.update(current => {
      const idx = current.findIndex(item => item.orderId === view.orderId);
      if (idx === -1) return [view, ...current];
      const copy = [...current];
      copy[idx] = view;
      return copy;
    });
  }

  private toView(order: OrderApiItem): OrderView {
    return {
      orderId: order.orderId,
      phone: order.phone,
      customerName: order.customerName,
      displayName: order.displayName,
      currency: order.currency,
      items: order.items,
      total: order.total,
      status: order.status,
      createdAt: order.createdAt,
    };
  }
}
